import React, { useState, useEffect, useRef } from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import LoginPopup from './popup/LoginPopup';
import SEO from '../components/common/SEO';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const CONTACT_NUMBER = process.env.REACT_APP_CONTACT_NUMBER || '9699480470';
const EMAIL_US = process.env.REACT_APP_EMAIL_US || 'help@mangoloops.com';


const slugify = (str) =>
  str
    ? String(str)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '')
    : '';

// New hotel slug format: <hotelname>-<city>-<price>-<id>
const generateHotelSlug = (hotel) => {
  const name = slugify(hotel?.hotel_name || hotel?.name);
  const city = slugify(hotel?.city || hotel?.location_city);
  const price = Math.round(hotel?.offer_price || hotel?.hotel_price || hotel?.price || 0);
  const id = hotel?.id;

  return [name, city, price ? price : null, id]
    .filter(Boolean)
    .join('-')
    .replace(/--+/g, '-');
};


function HotelDetails() {
  const { id: idParam, slug } = useParams();

  // Support both route styles:
  // 1) /hotel-details/:id (idParam)
  // 2) /hotel-details/:slug where slug is <hotelname>-<city>-<price>-<id>
  // Extract hotel id from the last hyphen-separated token.
  const id = idParam || (slug ? (() => {
    const parts = slug.split('-').filter(Boolean);
    const last = parts[parts.length - 1];
    return /^\d+$/.test(last) ? last : null;
  })() : null);

  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  const navigate = useNavigate();

  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const mobileGalleryRef = useRef(null);

  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const thumbnails = hotel?.Images ? hotel.Images.filter(img =>
    img.image_size_name === 'thumbnail' || img.image_url.includes('_thumbnail.webp')
  ) : [];
  let displayThumbnails = thumbnails;
  if (thumbnails.length > 0 && thumbnails.length < 5) {
    displayThumbnails = Array(Math.ceil(5 / thumbnails.length)).fill(thumbnails).flat().slice(0, 5);
  } else if (thumbnails.length > 5) {
    displayThumbnails = thumbnails.slice(0, 5);
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-slide main image on mobile view (changes image every 3 seconds)
  useEffect(() => {
    if (displayThumbnails.length <= 1) return;

    const interval = setInterval(() => {
      if (window.innerWidth <= 991) {
        setActiveIndex((currentIdx) => {
          const nextIdx = (currentIdx + 1) % displayThumbnails.length;
          const imgUrl = displayThumbnails[nextIdx]?.image_url || displayThumbnails[nextIdx];
          const largeUrl = imgUrl?.replace('_thumbnail.webp', '_large.webp');
          setMainImage(largeUrl);
          return nextIdx;
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [displayThumbnails]);

  // Smoothly scroll the active thumbnail into center view on mobile scrollbar
  useEffect(() => {
    if (window.innerWidth <= 991 && mobileGalleryRef.current) {
      const galleryEl = mobileGalleryRef.current;
      const activeImg = mobileGalleryRef.current.children[activeIndex];
      if (activeImg) {
        galleryEl.scrollTo({
          left: activeImg.offsetLeft - (galleryEl.clientWidth - activeImg.clientWidth) / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [activeIndex]);


  // Sidebar: other hotel packages
  const [otherHotels, setOtherHotels] = useState([]);
  const [otherHotelsLoading, setOtherHotelsLoading] = useState(true);


  const [reviewsSummary, setReviewsSummary] = useState({ count: 0, avgRating: 0 });
  const [reviewsList, setReviewsList] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [sortOption, setSortOption] = useState('most_relevant');

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [newReviewText, setNewReviewText] = useState('');

  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserReview, setCurrentUserReview] = useState(null);

  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem('authToken') || localStorage.getItem('token') || null;

  useEffect(() => {
    // Open handler uses currentUser; keep it fresh if login happens.
    // (No-op here, but ensures hook ordering remains stable.)
  }, []);


  const handleAction = (e, actionType) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isSubmittingQuery) return;

    setPendingAction(actionType);
    setIsSubmittingQuery(true);

    if (!currentUser) {
      setShowLoginPopup(true);
      return;
    }
    processLead(actionType, currentUser);
  };

  const processLead = async (actionType, userData = null) => {
    setIsSubmittingQuery(true);
    const sessionUser = userData || currentUser;

    const hotelName = hotel?.hotel_name || hotel?.name || 'Hotel';
    const hotelCity = hotel?.city || hotel?.location_city || '';

    const payload = {
      name: sessionUser?.name || 'Guest',
      email: sessionUser?.email || '',
      mobile: (sessionUser?.phone || sessionUser?.mobile || '').replace(/^(\+91|91)/, ''),
      city: hotelCity,
      type: 'hotel',
      type_id: id,
      message: `User clicked ${actionType} for Hotel: ${hotelName}`,
    };

    let success = false;
    try {
      const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = currentToken ? { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const response = await axios.post(`${API_BASE_URL}/enquiries/create`, payload, { headers });

      if (response.data?.success || response.status === 200 || response.status === 201) {
        success = true;
        setShowThankYouModal(true);
      }
    } catch (err) {
      console.error('Lead submission error:', err);
      alert(err.response?.data?.message || 'Failed to process request. Please try again.');
    } finally {
      setIsSubmittingQuery(false);
      if (!success) {
        setPendingAction(null);
      }
    }
  };

  const handleFinalRedirect = (actionType) => {
    setShowThankYouModal(false);
    const contactNumber = hotel?.Vendor?.phone || hotel?.Vendor?.whatsapp_no || hotel?.phone_number || hotel?.hotel_phone || hotel?.whatsapp_number || hotel?.hotel_whatsapp_number;
    const email = hotel?.Vendor?.email || hotel?.hotel_email || hotel?.email;

    const userName = currentUser?.name || 'Guest';
    const userPhone = currentUser?.phone || currentUser?.mobile || '';
    const userEmail = currentUser?.email || '';
    const whatsappMsg = encodeURIComponent(
      `Hi, I am ${userName} (Phone: ${userPhone}, Email: ${userEmail}). I am interested in ${hotel?.hotel_name}. Please share more details.`
    );
    const emailSubject = encodeURIComponent(`Enquiry for ${hotel?.hotel_name}`);
    const emailBody = encodeURIComponent(
      `Hi,\n\nI am ${userName} (Phone: ${userPhone}).\nI am interested in your hotel "${hotel?.hotel_name}".\nPlease share availability and pricing details.\n\nThank you.`
    );

    if (actionType === 'call' && contactNumber) {
      window.location.href = `tel:${contactNumber}`;
    } else if (actionType === 'whatsapp' && contactNumber) {
      window.open(`https://wa.me/${contactNumber}?text=${whatsappMsg}`, '_blank');
    } else if (actionType === 'email' && email) {
      window.location.href = `mailto:${email}?subject=${emailSubject}&body=${emailBody}`;
    } else if (actionType === 'book') {
      // Stay or show success
    }
    setPendingAction(null);
  };


  useEffect(() => {
    const loadUserFromToken = async () => {
      if (!token) return;

      try {
        const decoded = jwtDecode(token);
        const userId = decoded.userId || decoded.id;

        if (!userId) {
          setCurrentUser(null);
          return;
        }

        // Backend currently exposes:
        // - GET /api/users (all)
        // - GET /api/users/:id (empty stub in userRoute.js)
        // So we cannot rely on /:id.
        // Best-effort: try localStorage first, then fall back to /api/users and find by id.
        const cached = localStorage.getItem('userInfo');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const resolved = parsed?.user?.user || parsed?.user || parsed;
            if (resolved?.id === userId || resolved?.userId === userId) {
              setCurrentUser(resolved);
              // keep localStorage shape consistent with Header.js
              try {
                const existing = localStorage.getItem('userInfo');
                if (!existing) {
                  localStorage.setItem('userInfo', JSON.stringify({ user: { user: resolved } }));
                } else {
                  const parsed = JSON.parse(existing);
                  const already = parsed?.user?.user;
                  if (!already) localStorage.setItem('userInfo', JSON.stringify({ user: { user: resolved } }));
                }
              } catch { }
              return;
            }
          } catch {
            // ignore cache parse errors
          }
        }

        try {
          const res = await axios.get(`${API_BASE_URL}/users/`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const users = Array.isArray(res.data) ? res.data : res.data?.users;
          const match = Array.isArray(users) ? users.find((u) => String(u.id) === String(userId)) : null;
          const resolvedUser = match || { id: userId };
          setCurrentUser(resolvedUser);
          // keep localStorage shape consistent with Header.js
          try { localStorage.setItem('userInfo', JSON.stringify({ user: { user: resolvedUser, token } })); } catch { }

        } catch (e) {
          // If backend blocks this call, still set minimal data from token
          setCurrentUser({
            id: userId,
            name: decoded.userName || decoded.name,
            email: decoded.email,
            phone: decoded.phone,
            mobile: decoded.phone || decoded.mobile,
          });
        }
      } catch (error) {
        console.error('Failed to decode token:', error);
        setCurrentUser(null);
      }
    };

    loadUserFromToken();
  }, [token]);

  // Activity Lead Recording
  useEffect(() => {
    if (currentUser && hotel && hotel.id && !sessionStorage.getItem(`activity_lead_hotel_${hotel.id}`)) {
      const sendActivityLead = async () => {
        try {
          const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
          const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
          
          await axios.post(`${API_BASE_URL}/enquiries/activity`, {
            type: 'hotel',
            type_id: hotel.id,
            mobile: currentUser.phone || currentUser.mobile || '',
            name: currentUser.name || 'Guest',
            email: currentUser.email || '',
            city: hotel.city || ''
          }, { headers });
          
          sessionStorage.setItem(`activity_lead_hotel_${hotel.id}`, 'true');
        } catch (e) {
          console.error("Activity lead error:", e);
        }
      };
      
      // We need to make sure currentUser actually has mobile set (if it was just from token, it might have it)
      // Send it only if we have at least a mobile number
      if (currentUser.phone || currentUser.mobile) {
        sendActivityLead();
      }
    }
  }, [currentUser, hotel]);


  const parseReviewsBody = (body) => {
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.reviews)) return body.reviews;
    if (Array.isArray(body.data?.reviews)) return body.data.reviews;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.items)) return body.items;
    const arrKey = Object.keys(body).find((k) => Array.isArray(body[k]));
    if (arrKey) return body[arrKey];
    return [];
  };

  useEffect(() => {
    if (showLoginPopup) console.log('Login popup is visible');
  }, [showLoginPopup]);

  const fetchReviews = async () => {
    setReviewsLoading(true);
    setCurrentUserReview(null);
    try {
      const keyToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = keyToken ? { Authorization: `Bearer ${keyToken}` } : {};

      const url = `${API_BASE_URL}/hotels/${id}/reviews`;
      const res = await axios.get(url, { headers });
      const body = res.data ?? res;
      const reviews = parseReviewsBody(body);

      if (typeof body.count !== 'undefined' || typeof body.avgRating !== 'undefined') {
        setReviewsSummary({
          count: Number(body.count) || 0,
          avgRating: Number(body.avgRating) || 0
        });
      } else if (body.summary) {
        setReviewsSummary({
          count: Number(body.summary.count) || 0,
          avgRating: Number(body.summary.avgRating) || 0
        });
      }

      const userIds = Array.from(new Set(reviews.map((r) => r.user_id || r.userId || r.user?.id || r.User?.id).filter(Boolean)));
      const missingIds = userIds.filter((uid) => {
        const reviewWithUser = reviews.find((r) => r.User && r.User.id === uid);
        return !reviewWithUser && !usersMap[uid];
      });

      if (missingIds.length > 0) {
        const newUsersMap = { ...usersMap };
        try {
          const batchUrl = `${API_BASE_URL}/users?ids=${missingIds.join(',')}`;
          const batchRes = await axios.get(batchUrl, { headers });
          let fetchedUsers = [];
          if (Array.isArray(batchRes.data)) fetchedUsers = batchRes.data;
          else if (Array.isArray(batchRes.data.users)) fetchedUsers = batchRes.data.users;
          else if (Array.isArray(batchRes.data.data)) fetchedUsers = batchRes.data.data;
          fetchedUsers.forEach((u) => {
            if (u.id) newUsersMap[u.id] = u;
          });
          setUsersMap(newUsersMap);
        } catch (batchErr) {
          console.warn('Batch user fetch failed, falling back to individual fetches.', batchErr);
        }
      }

      setReviewsList(reviews || []);

      if (currentUser && reviews.length > 0) {
        const foundReview = reviews.find(r => String(r.user_id || r.userId || r.user?.id || r.User?.id) === String(currentUser.id));
        if (foundReview) {
          setCurrentUserReview(foundReview);
        }
      }
    } catch (err) {
      console.error('fetchReviews -> error', err);
      setReviewsList([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const openReviewsModal = async () => {
    // Check if user is logged in
    if (!currentUser && !token) {
      setShowLoginPopup(true);
      return;
    }

    setReviewsModalOpen(true);
    setShowWriteForm(false);
    await fetchReviews();
  };

  const handleWriteClick = () => {
    if (!token) {
      setReviewsModalOpen(false);
      setShowWriteForm(false);
      setShowLoginPopup(true);
      return;
    }

    if (currentUserReview) {
      setNewRating(currentUserReview.rating);
      setNewReviewText(currentUserReview.review_text || '');
      setIsEditing(true);
    } else {
      setNewRating(5);
      setNewReviewText('');
      setIsEditing(false);
    }
    setShowWriteForm(true);
  };

  const handleLoginSuccess = async (loggedInUser) => {
    setShowLoginPopup(false);

    // Refresh current user and decode new token
    const newToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (newToken) {
      try {
        const decoded = jwtDecode(newToken);
        const resolved = {
          id: decoded.userId || decoded.id,
          name: decoded.userName || decoded.name,
          email: decoded.email,
          phone: decoded.phone,
          mobile: decoded.phone || decoded.mobile
        };
        setCurrentUser(resolved);

        if (pendingAction) {
          processLead(pendingAction, resolved);
        } else {
          // Open review modal after successful login
          setTimeout(async () => {
            setReviewsModalOpen(true);
            setShowWriteForm(false);
            await fetchReviews();
          }, 300);
        }
      } catch (error) {
        console.error("Failed to decode new token:", error);
      }
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newRating || newRating < 1 || newRating > 5) {
      alert('Please choose a rating between 1 and 5.');
      return;
    }

    const reviewData = { rating: newRating, review_text: newReviewText };
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      let res;
      if (isEditing && currentUserReview) {
        // CORRECTED LINE: Use the hotel ID (`id`) from useParams
        const updateUrl = `${API_BASE_URL}/hotels/${id}/reviews`;
        res = await axios.put(updateUrl, reviewData, { headers });
      } else {
        const createUrl = `${API_BASE_URL}/hotels/${id}/reviews`;
        res = await axios.post(createUrl, reviewData, { headers });
      }

      if (res.status === 200 || res.status === 201) {
        await fetchReviews();
        await fetchReviewSummary();
        setShowWriteForm(false);
        setIsEditing(false);
      } else {
        alert(res.data?.message || 'Unable to submit review.');
      }
    } catch (err) {
      console.error('handleSubmitReview -> error', err);
      alert(err.response?.data?.message || 'Network/server error while submitting review.');
    }
  };

  const fetchReviewSummary = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/hotels/${id}/reviews?summary=true`);
      const body = res.data || {};
      if (typeof body.count !== 'undefined' || typeof body.avgRating !== 'undefined') {
        setReviewsSummary({ count: Number(body.count) || 0, avgRating: Number(body.avgRating) || 0 });
      } else if (body.data) {
        setReviewsSummary({ count: Number(body.data.count) || 0, avgRating: Number(body.data.avgRating) || 0 });
      }
    } catch (err) {
      console.error('fetchReviewSummary -> error', err);
    }
  };

  useEffect(() => {
    const fetchHotel = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotels/${id}`);
        setHotel(response.data);
        if (response.data.Images && response.data.Images.length > 0) {
          const largeImg = response.data.Images.find(img => img.image_size_name === 'large');
          setMainImage(largeImg ? largeImg.image_url : response.data.Images[0].image_url);
        }
      } catch (error) {
        console.error('fetchHotel -> error', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHotel();
    fetchReviewSummary();
  }, [id]);

  // Fetch “other hotel packages” for sidebar
  useEffect(() => {
    if (!id) return;

    const fetchOtherHotels = async () => {
      setOtherHotelsLoading(true);
      try {
        // Best-effort endpoint: match holiday sidebar pattern
        // If backend doesn't support this route, we'll fallback to empty list.
        const res = await axios.get(`${API_BASE_URL}/hotels/${id}/other-hotels?limit=5`);
        const data = res.data;

        // Common response shapes
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.hotels)
            ? data.hotels
            : Array.isArray(data.data)
              ? data.data
              : Array.isArray(data.otherHotels)
                ? data.otherHotels
                : [];

        setOtherHotels(list);
      } catch (e) {
        console.error('fetchOtherHotels -> error', e);
        setOtherHotels([]);
      } finally {
        setOtherHotelsLoading(false);
      }
    };

    fetchOtherHotels();
  }, [id]);


  const changeImage = (src, idx) => {
    // If it's a thumbnail URL, convert it to large
    const largeUrl = src.replace('_thumbnail.webp', '_large.webp');
    setMainImage(largeUrl);
    if (idx !== undefined) {
      setActiveIndex(idx);
    }
  };

  const renderStars = (rating, size = 16) => {
    const stars = [];
    const full = Math.floor(rating || 0);
    const half = (rating || 0) - full >= 0.5;
    for (let i = 1; i <= 5; i++) {
      if (i <= full) {
        stars.push(<i key={i} className="fas fa-star" style={{ color: '#f4b400', fontSize: size, marginRight: 4 }} />);
      } else if (i === full + 1 && half) {
        stars.push(<i key={i} className="fas fa-star-half-alt" style={{ color: '#f4b400', fontSize: size, marginRight: 4 }} />);
      } else {
        stars.push(<i key={i} className="far fa-star" style={{ color: '#f4b400', fontSize: size, marginRight: 4, opacity: 0.5 }} />);
      }
    }
    return <span style={{ display: 'inline-flex', alignItems: 'center' }}>{stars}</span>;
  };
  // thumbnails and displayThumbnails declared above

  // Also fix main image initial selection if it fails
  useEffect(() => {
    if (hotel && hotel.Images && hotel.Images.length > 0 && !mainImage) {
      const largeImg = hotel.Images.find(img => img.image_size_name === 'large' || img.image_url.includes('_large.webp'));
      setMainImage(largeImg ? largeImg.image_url : hotel.Images[0].image_url);
    }
  }, [hotel, mainImage]);

  const getAmenityIcon = (amenity) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi') || amenityLower.includes('internet')) return 'fa fa-wifi';
    if (amenityLower.includes('breakfast') || amenityLower.includes('meal') || amenityLower.includes('dining')) return 'fa fa-utensils';
    if (amenityLower.includes('parking')) return 'fa fa-car';
    if (amenityLower.includes('pool') || amenityLower.includes('swimming')) return 'fa fa-swimming-pool';
    if (amenityLower.includes('ac') || amenityLower.includes('air conditioning') || amenityLower.includes('cooling')) return 'fa fa-snowflake';
    if (amenityLower.includes('gym') || amenityLower.includes('fitness')) return 'fa fa-dumbbell';
    if (amenityLower.includes('tv') || amenityLower.includes('television')) return 'fa fa-tv';
    if (amenityLower.includes('bar') || amenityLower.includes('drink')) return 'fa fa-glass-martini';
    if (amenityLower.includes('spa') || amenityLower.includes('massage')) return 'fa fa-spa';
    if (amenityLower.includes('restaurant')) return 'fa fa-utensils';
    if (amenityLower.includes('room service')) return 'fa fa-bell';
    if (amenityLower.includes('laundry')) return 'fa fa-tshirt';
    if (amenityLower.includes('elevator') || amenityLower.includes('lift')) return 'fa fa-arrows-v';
    if (amenityLower.includes('pet')) return 'fa fa-paw';
    if (amenityLower.includes('phone') || amenityLower.includes('telephone') || amenityLower.includes('contact')) return 'fa fa-phone';
    if (amenityLower.includes('security') || amenityLower.includes('cctv')) return 'fa fa-shield-alt';
    return 'fa fa-check-circle';
  };

  if (loading) return <p className="text-center py-5">Loading hotel details...</p>;
  if (!hotel) return <p className="text-center py-5">Hotel not found.</p>;

  const amenities = typeof hotel.amenities === 'string' ? (() => { try { return JSON.parse(hotel.amenities); } catch { return []; } })() : hotel.amenities || [];

  return (
    <div className="hotel-detail-page">
      <SEO title={`${hotel.hotel_name} | Mangoloops`} description={hotel.hotel_overview || hotel.description} />
      <Header />
      <main id="content">
        <div className="banner-content container pt-3 holidays-left">
          <div className="row pb-5">
            <div className="col-lg-12 pb-3">
              <div className="row">
                <div className="mflex acenter justify-content-between hotel-detail-title-row">
                  <div className="hotel-detail-title-stack">
                    <span className="pkgname ng-binding">{hotel.hotel_name}</span>
                  </div>
                  <div className="desktop-only ml-3" role="button" tabIndex={0} onClick={openReviewsModal} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openReviewsModal(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                    {renderStars(reviewsSummary.avgRating, 18)}
                    <span className="rating-text" style={{ marginLeft: 4, fontWeight: 600 }}>{Number(reviewsSummary.avgRating || 0).toFixed(1)}/5</span>
                    <span className="rating-text" style={{ marginLeft: 8, color: '#666' }}>({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})</span>
                  </div>
                </div>
                <div className="stycitydtl pt-2">
                  <i className="fa fa-map-marker-alt" /> {hotel.address} {hotel.city}, {hotel.state}, {hotel.country} {hotel.pincode}
                </div>
              </div>
            </div>
            <div className="col-lg-8 mb-5">
              <div className="tour-package-container">
                <div className="tour-package-image hotel-main-photo-wrap">
                  <img
                    id="main-image"
                    src={mainImage || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                    alt={hotel.hotel_name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                    }}
                  />
                  <button
                    type="button"
                    className="detail-photo-review-badge"
                    onClick={openReviewsModal}
                    aria-label="Open hotel reviews"
                  >
                    <span className="detail-photo-review-stars">
                      {renderStars(reviewsSummary.avgRating || 0, 15)}
                    </span>
                    <span className="detail-photo-review-score">
                      {Number(reviewsSummary.avgRating || 0).toFixed(1)}/5
                    </span>
                    <span className="detail-photo-review-count">
                      ({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})
                    </span>
                  </button>
                </div>
                {/* Desktop Gallery (exactly 5 thumbnails) */}
                {displayThumbnails.length > 0 && (
                  <div className="image-gallery desktop-only d-flex mt-2" style={{ gap: '13px' }}>
                    {displayThumbnails.map((img, idx) => (
                      <img
                        key={idx}
                        className="thumbnail"
                        src={img.image_url}
                        alt={`hotel-${idx}`}
                        onClick={() => changeImage(img.image_url, idx)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: 90,
                          cursor: 'pointer',
                          border: idx === activeIndex ? '2px solid #007bff' : '1px solid #ddd',
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Mobile Gallery (scrollable, showing 2 at a time, auto & manual) */}
                {displayThumbnails.length > 0 && (
                  <div ref={mobileGalleryRef} className="image-gallery-mobile mobile-only mt-2">
                    {displayThumbnails.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.image_url}
                        alt={`hotel-thumb-${idx}`}
                        onClick={() => changeImage(img.image_url, idx)}
                        className={idx === activeIndex ? 'active' : ''}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <hr style={{ margin: '16px 0', borderColor: '#dee2e6', borderWidth: '1px' }} />
              <div className="row pt-2">
                <ul className="nav nav-pills gap-18 mb-3">
                  <li className="nav-item" role="presentation"><button className={`nav-link tour-details-nav-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')} style={{ border: activeTab === 'overview' ? 'none' : '1px solid #dee2e6', borderRadius: '20px', padding: '6px 18px' }}>Overview</button></li>
                  <li className="nav-item"><button className={`nav-link tour-details-nav-btn ${activeTab === 'amenities' ? 'active' : ''}`} onClick={() => setActiveTab('amenities')} style={{ border: activeTab === 'amenities' ? 'none' : '1px solid #dee2e6', borderRadius: '20px', padding: '6px 18px' }}>Amenities</button></li>
                  <li className="nav-item"><button className={`nav-link tour-details-nav-btn ${activeTab === 'policy' ? 'active' : ''}`} onClick={() => setActiveTab('policy')} style={{ border: activeTab === 'policy' ? 'none' : '1px solid #dee2e6', borderRadius: '20px', padding: '6px 18px' }}>Booking Policy</button></li>
                </ul>
                <div className="boxv1 mt-0">
                  {activeTab === 'overview' && (
                    <div className="tab-pane fade show active mb-3">
                      <h5 className="tour-details-h5">{hotel.hotel_name}</h5>
                      <div className="description-p" dangerouslySetInnerHTML={{ __html: hotel.hotel_overview || hotel.description }}></div>
                    </div>
                  )}
                  {activeTab === 'amenities' && <div className="tab-pane fade show active mb-4"><h5 className="tour-details-h5">Our Exclusive Amenities:</h5>{amenities.length > 0 ? <ul className="list-group list-group-borderless list-group-horizontal list-group-flush no-gutters row">{amenities.map((a, idx) => <li key={idx} className="col-md-3 list-group-item"><div className="d-flex align-items-center"><i className={`${getAmenityIcon(a)} mr-3 text-primary`} />{a}</div></li>)}</ul> : <p>No amenities listed.</p>}</div>}
                  {activeTab === 'policy' && (
                    <div className="tab-pane fade show active mb-3">
                      <h5 className="tour-details-h5">Booking Policy</h5>
                      <div dangerouslySetInnerHTML={{ __html: hotel.booking_policy || 'No booking policy available.' }}></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <aside className="sidebar-area sticky">
                <div className="mb-1">

                  <div className="pricecolm">
                    <div className="pda10 grbg bdrbtm">
                      <div className="mflex bg-color-btn">
                        <div className="price-sec">
                          <p className="strt">Starting from</p>

                          <p className="tlprice">
                            <span ng-bind="packDetails.currSymbol" className="ng-binding">₹ </span>
                            <span className="ng-binding">
                              {hotel?.offer_price ? Math.round(hotel.offer_price) : (hotel?.hotel_price ? Math.round(hotel.hotel_price) : 'Price as per request')}
                            </span>
                            <small className="pptxt">Per Night</small>
                          </p>

                          <p className="famous-places-sidebar">
                            Famous Attractions and Cultural Highlights of {hotel?.city || ''}
                          </p>

                          <div className="stage" id="pricewaitd" style={{ display: 'none' }}>
                            <div className="dot-pulse"></div>
                          </div>

                          <div className="clr"></div>
                        </div>
                      </div>
                    </div>

                    <div className="pda13 d-flex flex-column gap-2">
                      <button
                        className="sbmtbtn submit-query-btn hotel-detail-query-btn w-100"
                        id="myBt"
                        onClick={(e) => handleAction(e, "query")}
                        disabled={isSubmittingQuery}
                        style={{ cursor: 'pointer', margin: 0, padding: '12px 15px', borderRadius: '4px', fontSize: '16px', fontWeight: 'bold' }}
                      >
                        {isSubmittingQuery && pendingAction === "query" ? (
                          <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px', display: 'inline-block', width: '1rem', height: '1rem', verticalAlign: 'text-bottom', border: '.2em solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spinner-border .75s linear infinite' }}></span>
                        ) : null}
                        {isSubmittingQuery && pendingAction === "query" ? 'Submitting...' : 'Submit Your Query'}
                      </button>
                    </div>

                    <div className="clr"></div>
                  </div>

                  {hotel?.places_to_visit && (
                    <div className="main-rg-hol-1 places-to-visit-main-sec mgt15 mb-4">
                      <h5>Places to Visit Nearby:</h5>
                      <p>{hotel.places_to_visit}</p>
                    </div>
                  )}

                  <div className="">
                    <div className="tour-packages mt-4">
                      <h2 className="tour-packasge-sec-title">
                        {otherHotels?.length > 0 && otherHotels?.some(op => op.city && op.city.toLowerCase() === (hotel?.city || '').toLowerCase())
                          ? `Other ${hotel?.city} Hotels`
                          : 'Other Hotels'}
                      </h2>

                      <div className="packages">
                        {otherHotelsLoading ? (
                          <div className="text-center p-3">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : otherHotels?.length > 0 ? (
                          otherHotels.map((op) => (
                            <Link to={`/hotel-details/${generateHotelSlug(op)}`} className="package" key={op.id}>


                              <img
                                src={op.image || op.image_url || op.Images?.[0]?.image_url || '/no_image_hotel.jpg'}
                                alt={op.hotel_name || op.name || 'Hotel'}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/no_image_hotel.jpg';
                                }}
                              />
                              <div className="details">
                                <p>{op.hotel_name || op.name}</p>
                                <span>₹ {Math.round(op.offer_price || op.hotel_price || op.price || 0)}</span>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <p className="p-3 text-muted">No other hotels available.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Need Help div removed completely */}

                </div>
              </aside>
            </div>

          </div>
        </div>
      </main>

      {reviewsModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }} onClick={() => { setReviewsModalOpen(false); setShowWriteForm(false); fetchReviewSummary(); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: 0, borderRadius: 8, width: 920, maxWidth: '96%', maxHeight: '88vh', overflowY: 'auto', position: 'relative', zIndex: 100000, boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>

            <div style={{ padding: '18px 22px 0px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22 }}>{hotel.hotel_name}</h2>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 20 }}>{Number(reviewsSummary.avgRating || 0).toFixed(1)}</strong>
                  <span>{renderStars(reviewsSummary.avgRating || 0, 18)}</span>
                  <span style={{ color: '#666' }}>({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})</span>
                </div>
                <div style={{ color: '#666', marginTop: 8, fontSize: 14 }}><i className="fa fa-map-marker-alt" /> {hotel.address} {hotel.city}, {hotel.state}, {hotel.country} {hotel.pincode}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleWriteClick} style={{ padding: "8px 14px", borderRadius: "20px", border: "1px solid rgb(0, 123, 255)", background: "#fff", color: "rgb(0, 123, 255)", cursor: "pointer", whiteSpace: "nowrap" }}>
                  {currentUserReview ? 'Edit your review' : 'Write a review'}
                </button>
                <button onClick={() => { setReviewsModalOpen(false); setShowWriteForm(false); fetchReviewSummary(); }} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '12px 22px', borderBottom: '1px solid #f7f7f7' }}>
              <div style={{ marginBottom: 10, color: '#333' }}>Sort by</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['most_relevant', 'newest', 'highest', 'lowest'].map((opt) => {
                  const label = opt === 'most_relevant' ? 'Most relevant' : opt === 'newest' ? 'Newest' : opt === 'highest' ? 'Highest rating' : 'Lowest rating';
                  return <button key={opt} onClick={() => setSortOption(opt)} className={`btn ${sortOption === opt ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ borderRadius: 20, padding: '6px 12px', cursor: 'pointer' }}>{label}</button>;
                })}
              </div>
            </div>

            {showWriteForm && (
              <form onSubmit={handleSubmitReview} style={{ padding: '20px 22px', borderBottom: '1px solid #f7f7f7' }}>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Your Rating:</label>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    {[1, 2, 3, 4, 5].map((r) => <i key={r} className={r <= newRating ? 'fas fa-star' : 'far fa-star'} style={{ color: '#f4b400', fontSize: 30, cursor: 'pointer', transform: r <= newRating ? 'scale(1.15)' : 'scale(1)', transition: 'transform 0.12s' }} onClick={() => setNewRating(r)} />)}
                  </div>
                </div>
                <textarea value={newReviewText} onChange={(e) => setNewReviewText(e.target.value)} placeholder="Write your review..." rows={4} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd' }} />
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 12 }}>
                  <button type="submit" className="btn btn-primary" style={{ minWidth: 120 }}>{isEditing ? 'Update' : 'Post'}</button>
                  <button type="button" className="btn btn-secondary" style={{ minWidth: 120 }} onClick={() => { setShowWriteForm(false); setIsEditing(false); }}>Cancel</button>
                </div>
              </form>
            )}

            <div style={{ padding: '12px 22px 22px 22px' }}>
              {reviewsLoading ? <div>Loading reviews...</div> : reviewsList.length === 0 ? <div style={{ color: '#666', padding: '18px 0' }}>No reviews yet.</div> : (() => {
                const sorted = [...reviewsList];
                if (sortOption === 'newest') sorted.sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
                else if (sortOption === 'highest') sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                else if (sortOption === 'lowest') sorted.sort((a, b) => (a.rating || 0) - (b.rating || 0));
                return sorted.map((r) => {
                  const userObj = r.User || usersMap[r.user_id] || r.user || null;
                  const displayName = (userObj && (userObj.name || userObj.phone || userObj.username)) ? (userObj.name || userObj.phone || userObj.username) : 'Guest';
                  const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : 'G';
                  const createdAt = new Date(r.created_at || r.createdAt);
                  const formattedDate = isNaN(createdAt.getTime()) ? '' : createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                  return (
                    <div key={r.id || `${r.user_id}-${r.created_at}`} style={{ borderBottom: '1px solid #eee', paddingBottom: 12, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f1f1f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>{avatarLetter}</div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{displayName}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{formattedDate}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                          <div>{renderStars(r.rating, 16)}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{(r.rating || 0).toFixed(1)} ★</div>
                        </div>
                      </div>
                      {r.review_text && <div style={{ marginTop: 8 }}>{r.review_text}</div>}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
      {showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }} onClick={() => setShowLoginPopup(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <LoginPopup visible={showLoginPopup} onClose={() => setShowLoginPopup(false)} onLoginSuccess={handleLoginSuccess} />
          </div>
        </div>
      )}
      {(isSubmittingQuery || showThankYouModal) && !showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }} onClick={showThankYouModal ? () => setShowThankYouModal(false) : undefined}>
          <div style={{ background: 'white', padding: '40px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s ease' }} onClick={(e) => e.stopPropagation()}>
            {isSubmittingQuery && !showThankYouModal ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                    <span className="visually-hidden">Processing...</span>
                  </div>
                </div>
                <h4 style={{ fontWeight: 'bold', marginBottom: '10px' }}>Processing your request...</h4>
                <p style={{ color: '#6c757d', marginBottom: 0 }}>Please wait while we save your lead and notify the vendor.</p>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem', color: '#198754' }}></i>
                </div>
                <h2 style={{ fontWeight: 'bold', marginBottom: '12px' }}>Thank You!</h2>
                <p style={{ color: '#6c757d', marginBottom: '24px' }}>Your request has been sent to the team. We will get back to you shortly.</p>
                <button className="btn btn-primary" style={{ borderRadius: '50px', padding: '10px 30px', fontWeight: 'bold' }} onClick={() => setShowThankYouModal(false)}>
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <style>{`
        .hotel-detail-query-btn {
          background: #0b7fab !important;
          color: #fff !important;
          border: 0 !important;
          border-radius: 6px !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          letter-spacing: 0 !important;
          min-height: 49px;
          transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }
        .hotel-detail-query-btn:hover:not(:disabled) {
          background: #086f98 !important;
          transform: translateY(-1px);
        }
        .hotel-mobile-query-btn {
          flex-direction: row !important;
          gap: 6px;
          min-height: 46px;
          font-weight: 700;
        }
        .hotel-mobile-query-btn:disabled {
          opacity: 0.85;
          cursor: not-allowed;
        }
        .hotel-detail-title-stack {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
        }
        .hotel-detail-page #header {
          background: #fff;
          box-shadow: 0 2px 14px rgba(15, 23, 42, 0.12);
          position: relative;
          z-index: 10;
        }
        .hotel-detail-page #header .u-header__section {
          background: #fff !important;
          box-shadow: 0 6px 22px rgba(15, 23, 42, 0.14);
        }
        .hotel-main-photo-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 8px;
        }
        .detail-photo-review-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          display: none;
          align-items: center;
          gap: 5px;
          max-width: calc(100% - 20px);
          padding: 6px 8px;
          border: 0;
          border-radius: 5px;
          background: rgba(17, 24, 39, 0.72);
          color: #fff;
          cursor: pointer;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
          backdrop-filter: blur(3px);
          line-height: 1;
          z-index: 2;
        }
        .detail-photo-review-badge:hover {
          background: rgba(17, 24, 39, 0.84);
        }
        .detail-photo-review-stars {
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        .detail-photo-review-stars i {
          margin-right: 2px !important;
        }
        .detail-photo-review-score {
          font-weight: 700;
          font-size: 12px;
          white-space: nowrap;
        }
        .detail-photo-review-count {
          display: none;
        }
        @media (min-width: 992px) {
          .hotel-detail-page #header {
            position: fixed !important;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10;
            background: #fff;
          }
          .hotel-detail-page #header .u-header__section {
            background: #fff !important;
            box-shadow: 0 6px 22px rgba(15, 23, 42, 0.14);
          }
          .hotel-detail-page main#content > .banner-content.container {
            margin-top: 0 !important;
            padding-top: 132px !important;
          }
          .hotel-detail-title-row {
            align-items: flex-start !important;
            gap: 20px;
          }
          .hotel-detail-page main#content {
            background: #fff;
          }
        }
        @media (max-width: 768px) {
          .hotel-detail-title-row {
            align-items: flex-start !important;
          }
          .hotel-detail-title-stack {
            width: 100%;
          }
          .hotel-detail-page main#content > .banner-content.container {
            padding-top: 10px !important;
          }
          .hotel-detail-page .pricecolm {
            display: none !important;
          }
          .detail-photo-review-badge {
            display: inline-flex;
          }
        }
      `}</style>
      {/* Mobile Sticky Bottom Bar */}
      <div className={`holiday-mobile-sticky-bar ${showStickyBar ? 'visible' : ''}`}>
        <div className="holiday-sticky-price-info">
          <div className="holiday-sticky-price-row">
            <span className="holiday-sticky-price-current">
              ₹{hotel?.offer_price ? Math.round(hotel.offer_price).toLocaleString() : (hotel?.hotel_price ? Math.round(hotel.hotel_price).toLocaleString() : 'Price on request')}
            </span>
            {hotel?.offer_price && hotel?.hotel_price && Number(hotel.hotel_price) > Number(hotel.offer_price) && (
              <span className="holiday-sticky-price-strike">
                ₹{Math.round(hotel.hotel_price).toLocaleString()}
              </span>
            )}
          </div>
          {(hotel?.offer_price || hotel?.hotel_price) && (
            <div className="holiday-sticky-taxes-label">
              per night
            </div>
          )}
        </div>
        <div className="holiday-sticky-action-container">
          <button
            className="holiday-sticky-book-btn hotel-mobile-query-btn"
            onClick={(e) => handleAction(e, "query")}
            disabled={isSubmittingQuery}
          >
            {isSubmittingQuery && pendingAction === "query" ? (
              <>
                <span className="spinner-border spinner-border-sm" style={{ display: 'inline-block', width: '1rem', height: '1rem', verticalAlign: 'text-bottom', border: '.2em solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spinner-border .75s linear infinite' }}></span>
                <span className="btn-title-main">Processing...</span>
              </>
            ) : (
              <span className="btn-title-main">Enquire Now</span>
            )}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default HotelDetails;
